import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { where } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RoomTicket, TicketStatus } from '../models/ticket.model';
import { getPreview } from '../utility/ticket';
import { FirestoreService } from './firestore.service';

export interface TopTickets {
  completed: Record<string, RoomTicket[]>;
  created: Record<string, RoomTicket[]>;
  fastest: (RoomTicket & { durationMs: number; doneBy: string })[];
}

const itemCollection = 'tickets';
const url = `${environment.backendUrl}/tickets`;

const converter = {
  toFirestore: (item: RoomTicket) => item,
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options) as RoomTicket;
    data.preview$ = getPreview(data);
    return data;
  },
};

@Injectable({
  providedIn: 'root',
})
export class TicketsService {
  constructor(
    private firestore: FirestoreService,
    private http: HttpClient,
  ) { }

  async save(item: RoomTicket) {
    delete item.guid;
    return this.firestore.save<RoomTicket>(itemCollection, item);
  }

  async update(item: RoomTicket) {
    return this.firestore.update(itemCollection, item);
  }

  async delete(item: RoomTicket) {
    return this.firestore.delete(itemCollection, item);
  }

  get(guid: string): Observable<RoomTicket> {
    return this.firestore.get<RoomTicket>(itemCollection, guid);
  }

  getOnce(guid: string): Observable<RoomTicket> {
    return this.firestore.getOnce<RoomTicket>(itemCollection, guid);
  }

  getList(options?: {
    area?: string;
    site?: string;
    creator?: string;
    sites?: string[];
    client?: string;
    contractor?: string;
    status?: TicketStatus;
  }): Observable<RoomTicket[]> {
    if (options == null) {
      return of([]);
    }
    const queryConstraints = [];
    if (options.area) {
      queryConstraints.push(where('area.guid', '==', options.area));
    }
    if (options.site) {
      queryConstraints.push(where('site', '==', options.site));
    }
    if (options.creator) {
      queryConstraints.push(where('creator.guid', '==', options.creator));
    }
    if (options.sites) {
      queryConstraints.push(where('site', 'in', options.sites));
    }
    if (options.client) {
      queryConstraints.push(where('client', '==', options.client));
    }
    if (options.contractor) {
      queryConstraints.push(where('contractor.guid', '==', options.contractor));
    }
    if (options.status) {
      queryConstraints.push(where('status', '==', options.status));
    }
    return this.firestore.getList<RoomTicket>(itemCollection, undefined, queryConstraints, converter);
  }

  translateField(ticketGuid: string, field: string, history = false, note = false) {
    const body = {
      guid: ticketGuid,
      field,
      history,
      note,
    };
    return this.http.post(url, body);
  }

  getTop(site: string) {
    return this.http.get<TopTickets>(`${url}/top?site=${site}`);
  }
}
